/**
 * Normalizes a parameter name to the format used by the application and back end.
 *
 * Invalid characters are removed and the casing is adjusted to match how parameter
 * names are serialised as dictionary keys by the back end. This ensures the parameter
 * name remains consistent after a round trip.
 *
 * @param name - The parameter name to normalize.
 * @returns The normalized parameter name.
 */
export const normalizeParameterName = (name: string): string => {
  const normalizedName = name.replace(/[^a-zA-Z0-9_-]/g, "");

  if (normalizedName === "" || !isUpperCase(normalizedName[0])) {
    return normalizedName;
  }

  const chars = normalizedName.split("");

  for (let i = 0; i < chars.length; i++) {
    if (i === 1 && !isUpperCase(chars[i])) break;

    if (i > 0 && i + 1 < chars.length && !isUpperCase(chars[i + 1])) break;

    chars[i] = chars[i].toLowerCase();
  }

  return chars.join("");
};

/**
 * Checks whether a character is an uppercase letter.
 *
 * @param char - The character to check.
 * @returns Whether the character is an uppercase letter.
 */
const isUpperCase = (char: string): boolean =>
  char !== char.toLowerCase() && char === char.toUpperCase();
